import { Role } from '@anecdotes/api-contracts';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, defer, filter, shareReplay, switchMap, tap } from 'rxjs';
import { CalculatedRole } from '../../models/calculated-role';
import { RoleActions } from '../../store/actions';
import { RoleSelectors } from '../../store/selectors';
import { Dictionary } from '@ngrx/entity';

@Injectable()
export class RoleFacadeService {
  private defferedLoading$: Observable<any>;

  constructor(private store: Store) {
    this.defferedLoading$ = defer(() => this.store.pipe(
      select(RoleSelectors.selectShouldLoad),
      tap((shouldLoad) => {
        if (shouldLoad) {
          this.load();
        }
      }),
      filter((shouldLoad) => !shouldLoad),
      shareReplay({ bufferSize: 1, refCount: true })
    ));
  }

  load(): void {
    this.store.dispatch(RoleActions.loadRoles());
  }

  async createRoleAsync(role: Role): Promise<void> {
    this.store.dispatch(RoleActions.createRole({ role }));
  }

  editRole(role: Role): void {
    this.store.dispatch(RoleActions.updateRole({ role }));
  }

  deleteRole(role: Role): void {
    this.store.dispatch(RoleActions.deleteRole({ role }));
  }

  areRolesLoaded(): Observable<boolean> {
    return this.store.select(RoleSelectors.selectAreRolesLoaded);
  }

  getRolesDictionary(): Observable<Dictionary<CalculatedRole>> {
    return this.defferedLoading$
      .pipe(
        switchMap(() => this.areRolesLoaded()),
        filter(Boolean),
        switchMap(() =>  this.store.select(RoleSelectors.selectRolesDictionary)),
      );
  }

  getRoles(): Observable<CalculatedRole[]> {
    return this.defferedLoading$
      .pipe(
        switchMap(() => this.areRolesLoaded()),
        filter(Boolean),
        switchMap(() =>  this.store.select(RoleSelectors.selectRoles)),
      );
  }

  getRoleByRoleKey(roleKey: string): Observable<CalculatedRole> {
    return this.defferedLoading$
      .pipe(
        switchMap(() => this.areRolesLoaded()),
        filter(Boolean),
        switchMap(() =>  this.store.select(RoleSelectors.selectByRoleKey(roleKey))),
      );
  }
}
